/**
 * Live bucket-stats panel — super_admin ground-truth read of Tigris vs DB.
 *
 * Extracted verbatim from StorageFilesTab.jsx during the Phase 6 refactor
 * (no behaviour change).
 */
import { Loader2, AlertCircle, RefreshCw, Database, CloudCog, ChevronRight } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { formatBytes } from '../../../utils/uploadImage';

export function BucketStatsPanel({ isSuperAdmin, bucketStats, bucketStatsLoading, onScan }) {
  if (!isSuperAdmin) return null;
  return (
    <section className="rounded-lg border bg-card overflow-hidden" data-testid="bucket-stats-card">
      <div className="px-4 py-3 border-b flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <CloudCog className="w-4 h-4 text-[var(--lumyn-content)]" />
          <h3 className="font-semibold">Live bucket stats</h3>
          <span className="text-xs text-muted-foreground">verifies usage straight from object storage</span>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={onScan}
          disabled={bucketStatsLoading}
          data-testid="bucket-stats-refresh"
        >
          {bucketStatsLoading
            ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
            : <RefreshCw className="w-4 h-4 mr-1.5" />}
          {bucketStats ? 'Re-scan bucket' : 'Verify with bucket'}
        </Button>
      </div>

      {!bucketStats && !bucketStatsLoading && (
        <div className="px-4 py-6 text-sm text-muted-foreground">
          Click "Verify with bucket" to scan storage directly. Useful when the
          vendor dashboard doesn't match Lumyn's own counts.
        </div>
      )}

      {bucketStatsLoading && (
        <div className="px-4 py-6 flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" />
          Scanning bucket… this can take a few seconds for large buckets.
        </div>
      )}

      {bucketStats && bucketStats !== false && (
        <div className="px-4 py-4 space-y-4">
          {/* Top row: bucket coordinates */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
            <div className="rounded border bg-muted/30 px-3 py-2">
              <div className="uppercase tracking-wider text-muted-foreground">Bucket</div>
              <div className="font-mono text-sm mt-0.5" data-testid="bucket-stats-bucket">{bucketStats.bucket}</div>
            </div>
            <div className="rounded border bg-muted/30 px-3 py-2">
              <div className="uppercase tracking-wider text-muted-foreground">Endpoint</div>
              <div className="font-mono text-sm mt-0.5 truncate" title={bucketStats.endpoint} data-testid="bucket-stats-endpoint">
                {bucketStats.endpoint}
              </div>
            </div>
          </div>

          {/* DB-vs-bucket comparison — the headline. */}
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg border p-3" data-testid="bucket-stats-tigris">
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
                <CloudCog className="w-3 h-3" /> In bucket (ground truth)
              </div>
              <div className="text-2xl font-display font-bold tabular-nums mt-1">
                {formatBytes(bucketStats.total_bytes)}
              </div>
              <div className="text-xs text-muted-foreground tabular-nums">
                {bucketStats.object_count.toLocaleString()} objects
                {bucketStats.truncated && <span className="text-amber-600"> (scan capped — lower bound)</span>}
              </div>
            </div>
            <div className="rounded-lg border p-3" data-testid="bucket-stats-db">
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
                <Database className="w-3 h-3" /> In Lumyn DB
              </div>
              <div className="text-2xl font-display font-bold tabular-nums mt-1">
                {formatBytes(bucketStats.db_known_bytes)}
              </div>
              <div className="text-xs text-muted-foreground tabular-nums">
                {bucketStats.db_known_count.toLocaleString()} records
              </div>
            </div>
          </div>

          {/* Drift callout — shown only when the numbers diverge by
              more than a small noise floor. Most healthy buckets
              have a small positive drift (recently-deleted DB rows
              whose objects haven't been GC'd yet); a big drift
              means orphans accumulating. */}
          {(() => {
            const driftObjs = bucketStats.object_count - bucketStats.db_known_count;
            const driftBytes = bucketStats.total_bytes - bucketStats.db_known_bytes;
            if (Math.abs(driftObjs) < 5 && Math.abs(driftBytes) < 1024 * 1024) return null;
            const friendly = driftObjs > 0
              ? 'in the bucket but not in the DB (orphans — bucket-only).'
              : 'in the DB but not in the bucket (DB rows pointing to missing objects).';
            return (
              <div className="rounded-md border border-amber-300/60 bg-amber-50 dark:bg-amber-950/20 px-3 py-2 flex items-start gap-2" data-testid="bucket-stats-drift">
                <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-amber-900 dark:text-amber-200">
                  <span className="font-semibold tabular-nums">
                    {driftObjs >= 0 ? '+' : ''}{driftObjs.toLocaleString()} objects
                  </span>
                  {' '}({driftBytes >= 0 ? '+' : ''}{formatBytes(Math.abs(driftBytes))})
                  {' '}{friendly}
                </div>
              </div>
            );
          })()}

          {/* Timeline */}
          <div className="text-xs text-muted-foreground flex items-center gap-4 flex-wrap">
            {bucketStats.oldest_object_at && (
              <span>Oldest object: <span className="text-foreground">{new Date(bucketStats.oldest_object_at).toLocaleDateString()}</span></span>
            )}
            {bucketStats.newest_object_at && (
              <span>Newest: <span className="text-foreground">{new Date(bucketStats.newest_object_at).toLocaleDateString()}</span></span>
            )}
          </div>

          {/* Top prefixes — collapsible, "show where the bytes go" */}
          {bucketStats.top_prefixes?.length > 0 && (
            <details className="rounded border bg-muted/30 px-3 py-2" data-testid="bucket-stats-prefixes">
              <summary className="cursor-pointer text-xs font-medium text-muted-foreground select-none flex items-center gap-1">
                <ChevronRight className="w-3.5 h-3.5 transition-transform [details[open]_&]:rotate-90" />
                Top {bucketStats.top_prefixes.length} prefixes by size
              </summary>
              <table className="w-full text-xs mt-2 tabular-nums">
                <thead className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="text-left font-medium py-1">Prefix</th>
                    <th className="text-right font-medium py-1">Objects</th>
                    <th className="text-right font-medium py-1">Size</th>
                  </tr>
                </thead>
                <tbody>
                  {bucketStats.top_prefixes.map((p) => (
                    <tr key={p.prefix} className="border-t border-border/50">
                      <td className="py-1 font-mono truncate max-w-md" title={p.prefix}>{p.prefix}</td>
                      <td className="py-1 text-right">{p.count.toLocaleString()}</td>
                      <td className="py-1 text-right">{formatBytes(p.bytes)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          )}
        </div>
      )}
    </section>
  );
}
